import styled, { css } from 'styled-components';
import { zIndex } from 'styles';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { BORDER_BOX } from 'common';

const {
  SPACING,
  COLORS,
  SHADOW_SPREAD,
  OPACITY
} = Tokens;

const {
  SHADOW_SPREAD_0,
  SHADOW_SPREAD_1,
  SHADOW_SPREAD_6
} = SHADOW_SPREAD;

const {
  OPACITY_12,
  OPACITY_24
} = OPACITY;

const {
  SPACING_0,
  SPACING_2,
} = SPACING;

/* defaults */
const SHADOW_1 = `${SHADOW_SPREAD_0} ${SHADOW_SPREAD_1} ${SHADOW_SPREAD_1} ${SHADOW_SPREAD_0} rgba(0, 0, 0, ${OPACITY_24})`;
const SHADOW_2 = `${SHADOW_SPREAD_0} ${SHADOW_SPREAD_0} ${SHADOW_SPREAD_1} ${SHADOW_SPREAD_0} rgba(0, 0, 0, ${OPACITY_12})`;
const SHADOW_3 = `${SHADOW_SPREAD_0} ${SHADOW_SPREAD_0} ${SHADOW_SPREAD_0} ${SHADOW_SPREAD_6}`;

const getColor = (stateColor, checked, theme) => {
  let color;
  if (stateColor) {
    color = checked ? theme.secondary.ON.track : theme.secondary.OFF.track;
  } else {
    color = checked ? theme.primary.ON.track : theme.primary.OFF.track;
  }
  return color;
};

const getKnobColor = (stateColor, checked, theme) => {
  let color;
  if (stateColor) {
    color = checked ? theme.secondary.ON.knob : theme.secondary.OFF.knob;
  } else {
    color = checked ? theme.primary.ON.knob : theme.primary.OFF.knob;
  }
  return color;
};

const getDisabledColor = (stateColor, checked, theme) => {
  let color;
  if (stateColor) {
    color = checked ? theme.secondary.ON.disabledBackground : theme.secondary.OFF.disabledBackground;
  } else {
    color = checked ? theme.primary.ON.disabledBackground : theme.primary.OFF.disabledBackground;
  }
  return color;
};

const getTrackColor = ({ stateColor, checked, theme }) => {
  const color = getColor(stateColor, checked, theme);
  return css`background-color: ${color};`;
};

const getHoveredColor = (stateColor, checked, theme) => {
  let color;
  if (stateColor) {
    color = checked ? theme.secondary.ON.hoverBackground : theme.secondary.OFF.hoverBackground;
  } else {
    color = checked ? theme.primary.ON.hoverBackground : theme.primary.OFF.hoverBackground;
  }
  return color;
};

const getPressedColor = (stateColor, checked, theme) => {
  let color;
  if (stateColor) {
    color = checked ? theme.secondary.ON.pressedBackground : theme.secondary.OFF.pressedBackground;
  } else {
    color = checked ? theme.primary.ON.pressedBackground : theme.primary.OFF.pressedBackground;
  }
  return color;
};

const getToggleStyle = ({
  disabled, checked, stateColor, RTL, theme, $disableAnimation
}) => {
  const knobColor = getKnobColor(stateColor, checked, theme);
  const disabledColor = getDisabledColor(stateColor, checked, theme);
  const color = disabled ? disabledColor : knobColor;
  let transform;
  if (checked) {
    transform = !RTL ? 'translateX(1rem)' : 'translateX(-1rem)';
  } else {
    transform = 'translateX(0)';
  }

  return ({
    width: '1.25rem',
    height: '1.25rem',
    position: 'absolute',
    cursor: `${disabled ? 'unset' : 'pointer'}`,
    top: `${SPACING_0}`,
    borderRadius: '1.25rem',
    backgroundColor: `${color}`,
    transform,
    boxShadow: !disabled && `${SHADOW_1}, ${SHADOW_2}`,
    transition: $disableAnimation ? undefined : 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 0), box-shadow 0.3s cubic-bezier(0, 0, 0.2, 0), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 0)',
  });
};

const getInputStyle = ({
  disabled, checked, RTL
}) => {
  let transform;
  if (checked) {
    transform = !RTL ? 'translateX(1rem)' : 'translateX(-1rem)';
  } else {
    transform = 'translateX(0)';
  }

  return ({
    width: '1.25rem',
    height: '1.25rem',
    position: 'absolute',
    cursor: `${disabled ? 'unset' : 'pointer'}`,
    top: `${SPACING_0}`,
    borderRadius: '1.25rem',
    transform,
  });
};

const getTogglePseudoStyle = ({ pressed, stateColor, checked, theme }) => {
  const hoverColor = getHoveredColor(stateColor, checked, theme);
  const pressedColor = getPressedColor(stateColor, checked, theme);
  return css`box-shadow: ${SHADOW_1}, ${SHADOW_2}
  ${pressed ? `, ${SHADOW_3} ${pressedColor}` : `, ${SHADOW_3} ${hoverColor}`}; `;
};


const WRAPPER_STYLE = {
  height: '1.25rem',
  width: '2.25rem',
  position: 'relative',
};

const getTrackStyle = disabled => ({
  position: 'absolute',
  top: '0.1875rem',
  height: '0.875rem',
  width: '100%',
  borderRadius: '0.875rem',
  cursor: `${disabled ? 'unset' : 'pointer'}`,
});

export default {
  ToggleSwitchWrapper: styled.div`
  ${WRAPPER_STYLE}
  margin-right: 0.5rem;
  div:focus {
    outline: none;
  }
 `,

  Input: styled.input`
    ${props => getInputStyle(props)}
    background-color: transparent;
    box-shadow: unset;
    opacity: 0;
    z-index: ${zIndex.INPUT};
    max-width: 1.25rem;
    max-height: 1.25rem;
    min-width: 1.25rem;
    min-height: 1.25rem;
  `,

  Track: styled.div`
  ${props => getTrackStyle(props.disabled)}
  ${props => getTrackColor(props)}
  `,

  Toggle: styled.div`
  ${BORDER_BOX}
  ${props => getToggleStyle(props)}

  ${props => props.isHovered && getTogglePseudoStyle(props)}
  ${props => props.isFocused &&
    css`
    &::before {
      position: absolute;
      content: " ";
      top: -0.38rem;
      left: -0.38rem;
      background-color: ${COLORS.TRANSPARENT};
      border-radius: 100%;
      transition: 0.2s;
      height: 1.75rem;
      width: 1.75rem;
      border: ${SPACING_2} solid ${props.theme.focusedBackground};
    }
    `}
  `,

  ToolTipWrapper: styled.div`
    width: 3.375rem;
  `,
};